/**
 * RFC-003 / 父持总池：以父子层级结构获取组织下的项目预算树。
 * - `ProjectManagePanel` 折叠展示；
 * - 预算分拨目标为**根/父项目**（含 `hasSubprojects` 的父项），子项用 PATCH 设消耗上限。
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { orgCreditsApi } from '@/services/api/org-credits';
import type { BudgetTreeNode, OrgProjectBudgetScope } from '@/types/org';

export const orgProjectBudgetTreeKeys = {
  all: ['org-project-budget-tree'] as const,
  byOrg: (
    orgId: string,
    memberUserId?: string | null,
    projectScope?: OrgProjectBudgetScope,
    includeFolders = false
  ) =>
    [
      ...orgProjectBudgetTreeKeys.all,
      orgId,
      memberUserId ?? '__all__',
      projectScope ?? 'team',
      includeFolders ? 'folders' : 'plain',
    ] as const,
};

export function invalidateOrgProjectBudgetTreeQueries(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  orgId?: string | null
) {
  if (!orgId) return;
  return queryClient.invalidateQueries({ queryKey: [...orgProjectBudgetTreeKeys.all, orgId] });
}

export function useOrgProjectBudgetTree(
  orgId: string | null | undefined,
  enabled = true,
  memberUserId?: string | null,
  projectScope: OrgProjectBudgetScope = 'team',
  includeFolders = false
) {
  return useQuery<BudgetTreeNode[]>({
    queryKey: orgProjectBudgetTreeKeys.byOrg(
      orgId ?? '',
      memberUserId,
      projectScope,
      includeFolders
    ),
    queryFn: () =>
      orgCreditsApi.listProjectBudgetTree(orgId!, {
        memberUserId: memberUserId && memberUserId !== '' ? memberUserId : undefined,
        projectScope,
        includeFolders,
      }),
    enabled: enabled && !!orgId,
    staleTime: 30 * 1000,
  });
}

/** 递归把树拍平为 EPN 节点数组（子项目行等，非分拨目标列表）。 */
export function flattenEpnNodes(tree: BudgetTreeNode[]): BudgetTreeNode[] {
  const out: BudgetTreeNode[] = [];
  const visit = (nodes: BudgetTreeNode[]) => {
    for (const n of nodes) {
      if (n.isEpn) {
        out.push(n);
      }
      if (n.subprojects?.length) {
        visit(n.subprojects);
      }
    }
  };
  visit(tree);
  return out;
}

/** 分拨预算可选目标：仅顶层根项目（父池行，含含子项目的父项）。 */
export function listRootProjectAllocateOptions(tree: BudgetTreeNode[]) {
  return tree.map((n) => ({
    id: n.projectId,
    label: n.hasSubprojects ? `${n.projectName}（父项目 / 总池）` : n.projectName,
  }));
}
